import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { CloudArrowDownIcon, MagnifyingGlassCircleIcon, ShareIcon } from '@heroicons/react/24/outline';
import { userAppContext } from '@/context/Context';
import { ChartBarIcon } from '@heroicons/react/24/solid';
import { isOntologyGraphFeatureVisible } from '@/config/ontologyGraph';

const BUBBLE_SIZE = 152;
const RADIUS = 260;

type ToolDef = {
  id: string;
  title: string;
  description: string;
  hint: string;
  to: string;
  icon: React.ReactNode;
  roles?: string[];
  requiresOntologyGraph?: boolean;
};

const TOOLS: ToolDef[] = [
  {
    id: 'r-package',
    title: 'R Package',
    description: 'disgenet2r',
    hint: 'Install & explore',
    to: '/Tools?section=Rinstallation',
    icon: (
      <img
        src="/static/images/icons/rPackage.svg"
        alt="R Package"
        style={{ width: '1.875rem', height: '1.875rem' }}
      />
    ),
  },
  {
    id: 'api',
    title: 'REST API',
    description: 'Programmatic access',
    hint: 'Query the database',
    to: '/Tools?section=console',
    icon: (
      <img
        src="/static/images/icons/api-icon.svg"
        alt="API"
        style={{ width: '1.875rem', height: '1.875rem' }}
      />
    ),
  },
  {
    id: 'cytoscape',
    title: 'Cytoscape',
    description: 'App',
    hint: 'Visualize networks',
    to: '/Tools?section=installcyto',
    icon: (
      <img
        src="/static/images/icons/iconsPink/Cytoscape_logo.svg"
        alt="Cytoscape"
        style={{ width: '1.875rem', height: '1.875rem' }}
      />
    ),
  },
  {
    id: 'advanced-search',
    title: 'Advanced Search',
    description: 'Multi-entity query',
    hint: 'Complex queries',
    to: '/advancedSearch',
    icon: (
      <MagnifyingGlassCircleIcon
        style={{ width: '1.875rem', height: '1.875rem', color: 'var(--dna-magenta)' }}
      />
    ),
  },
  {
    id: 'enrichment',
    title: 'Enrichment',
    description: 'Analysis',
    hint: 'Gene set enrichment',
    to: '/Tools?section=enrichment',
    icon: (
      <ChartBarIcon
        style={{
          width: '1.875rem',
          height: '1.875rem',
          color: 'var(--dna-magenta)',
          transform: 'translateY(-2px) rotate(-90deg) rotateX(180deg)',
        }}
      />
    ),
  },
  {
    id: 'mcp-connector',
    title: 'MCP Connector',
    description: 'Data native to AI agents',
    hint: 'How to connect',
    to: '/Tools?section=mcp',
    icon: (
      <img
        src="/static/images/icons/iconsPink/062.svg"
        alt="mcp"
        style={{ width: '2.25rem', height: '2.25rem' }}
      />
    ),
  },
  {
    id: 'ontology-explorer',
    title: 'Ontology Explorer',
    description: 'Disease hierarchy',
    hint: 'Explore ontologies',
    to: '/ontology-graph',
    icon: <ShareIcon style={{ width: '1.875rem', height: '1.875rem', color: 'var(--dna-magenta)' }} />,
    requiresOntologyGraph: true,
  },
  {
    id: 'download',
    title: 'Download',
    description: 'DISGENET DB',
    hint: 'Full database',
    to: '/Tools?section=download',
    icon: (
      <CloudArrowDownIcon style={{ width: '1.875rem', height: '1.875rem', color: 'var(--dna-magenta)' }} />
    ),
    roles: ['ROLE_UNLIMITED', 'ROLE_DEVELOPER'],
  },
  {
    id: 'ai',
    title: 'AIDA',
    description: 'DISGENET AI',
    hint: 'Ask anything',
    to: '/Tools?section=assistant',
    icon: (
      <img src="/static/images/eyesAida.svg" alt="AIDA" style={{ width: '1.875rem', height: '1.875rem' }} />
    ),
  },
];

const CSS_VARS = { '--dna-magenta': 'rgb(209, 0, 145)' } as React.CSSProperties;

function ToolCard({ tool }: { tool: ToolDef }) {
  return (
    <motion.div whileHover={{ scale: 1.03 }} transition={{ type: 'spring', stiffness: 380, damping: 22 }}>
      <Link
        to={tool.to}
        className="flex flex-col rounded-2xl border border-gray-200 bg-white px-4 pt-4 shadow-sm hover:shadow-md transition-shadow"
        style={{ textDecoration: 'none', color: 'inherit', height: '150px' }}
      >
        <div className="flex flex-col items-center justify-center text-center">
          <span className="flex-shrink-0 mb-2">{tool.icon}</span>
          <div>
            <p className="font-bold text-sm leading-tight">{tool.title}</p>
          </div>
        </div>
        <p className="text-xs opacity-40 italic">{tool.hint}</p>
      </Link>
    </motion.div>
  );
}

function Tools() {
  const { user } = userAppContext();
  const userRoles: string[] = user?.['cognito:groups'] ?? [];

  const visibleTools = TOOLS.filter(tool => {
    if (tool.requiresOntologyGraph && !isOntologyGraphFeatureVisible) return false;
    if (!tool.roles) return true;
    return tool.roles.some(role => userRoles.includes(role));
  });

  const n = visibleTools.length;
  const hintSpace = 30;
  const side = (RADIUS + BUBBLE_SIZE / 2 + hintSpace) * 2;
  const cx = side / 2;
  const cy = side / 2;

  return (
    <div style={CSS_VARS}>
      <div className="flex flex-col items-center gap-6 px-4 py-10 md:hidden">
        <div className="text-center">
          <p className="text-xs font-semibold tracking-widest uppercase opacity-40 mb-1">DISGENET</p>
          <h1 className="text-4xl font-extrabold tracking-tight leading-none">Tools</h1>
          <p className="text-sm opacity-40 mt-2">Select a tool to get started</p>
        </div>
        <div className="grid grid-cols-2 gap-3 w-full max-w-sm">
          {visibleTools.map(tool => (
            <ToolCard key={tool.id} tool={tool} />
          ))}
        </div>
      </div>

      <div
        className="hidden md:flex items-center justify-center"
        style={{ minHeight: '93vh', padding: '1.5rem' }}
      >
        <div style={{ position: 'relative', width: side, height: side, maxWidth: '100%' }}>
          <svg
            style={{ position: 'absolute', inset: 0, width: '100%', height: '100%', pointerEvents: 'none' }}
          >
            <circle
              cx={cx}
              cy={cy}
              r={RADIUS}
              fill="none"
              stroke="rgba(0,0,0,0.07)"
              strokeWidth="1.5"
              strokeDasharray="5 12"
            />
          </svg>

          <div
            style={{
              position: 'absolute',
              top: cy,
              left: cx,
              transform: 'translate(-50%, -50%)',
              textAlign: 'center',
              pointerEvents: 'none',
              zIndex: 1,
            }}
          >
            <p
              style={{
                fontSize: '0.7rem',
                fontWeight: 600,
                letterSpacing: '0.15em',
                textTransform: 'uppercase',
                opacity: 0.4,
                marginBottom: '0.4rem',
              }}
            >
              DISGENET
            </p>
            <h1
              style={{
                fontSize: '2.8rem',
                fontWeight: 800,
                letterSpacing: '-0.03em',
                lineHeight: 1,
                margin: 0,
              }}
            >
              Tools
            </h1>
            <p style={{ fontSize: '0.78rem', opacity: 0.38, marginTop: '0.5rem' }}>
              Select a tool to get started
            </p>
          </div>

          {visibleTools.map((tool, i) => {
            const angleDeg = -90 + (360 / n) * i;
            const rad = (angleDeg * Math.PI) / 180;
            const left = cx + RADIUS * Math.cos(rad) - BUBBLE_SIZE / 2;
            const top = cy + RADIUS * Math.sin(rad) - BUBBLE_SIZE / 2;

            return (
              <motion.div
                key={tool.id}
                className="scrolly-s4-bubble"
                whileHover={{ scale: 1.07 }}
                transition={{ type: 'spring', stiffness: 380, damping: 22 }}
                style={{ position: 'absolute', left, top, width: BUBBLE_SIZE, cursor: 'pointer' }}
              >
                <Link to={tool.to} style={{ textDecoration: 'none', color: 'inherit' }}>
                  <div
                    className="scrolly-s4-bubble__ring"
                    style={{ width: BUBBLE_SIZE, height: BUBBLE_SIZE }}
                  >
                    <span className="scrolly-s4-bubble__icon">{tool.icon}</span>
                    <span className="scrolly-s4-bubble__title text-blue">{tool.title}</span>
                    <span className="scrolly-s4-bubble__desc">{tool.description}</span>
                  </div>
                  <span
                    className="scrolly-s4-bubble__hint mt-2"
                    style={{
                      display: 'block',
                      textAlign: 'center',
                      width: '100%',
                      color: 'var(--tertiary-gray-color',
                    }}
                  >
                    {tool.hint}
                  </span>
                </Link>
              </motion.div>
            );
          })}
        </div>
      </div>
    </div>
  );
}

export default Tools;
